
import type { Application } from '@/types/database.types';
import { hasRequiredFields } from '@/utils/applicationValidation';
import { SECTIONS, VALIDATION_SECTIONS } from '@/constants/application';

export const useApplicationProgress = (application: Application | null) => {
  const calculateProgress = () => {
    if (!application) return 0;
    
    // If the application is submitted, always return 100%
    if (application.status === 'submitted') {
      return 100;
    }
    
    // More conservative check for user interaction - require actual meaningful data
    const hasMeaningfulContact = !!(
      application.first_name && application.last_name && 
      application.email && application.phone
    );
    
    const hasMeaningfulCompany = !!(
      (application.company_legal_name || application.company_dba) &&
      application.naics_code
    );
    
    const hasMeaningfulEmployeeData = !!(
      application.w2_employee_count || 
      application.exempt_employee_count || 
      application.non_exempt_employee_count
    );
    
    // Only consider it "started" if user has filled substantial information in multiple areas
    const meaningfulSectionsStarted = [
      hasMeaningfulContact,
      hasMeaningfulCompany,
      hasMeaningfulEmployeeData
    ].filter(Boolean).length;
    
    // Require at least 2 meaningful sections to be started before showing progress
    if (meaningfulSectionsStarted < 2) {
      return 0;
    }
    
    console.log('User has meaningful progress on the application');
    
    // Check each section for completion
    let completedSections = 0;
    const totalSections = Object.keys(VALIDATION_SECTIONS).length;
    
    Object.entries(VALIDATION_SECTIONS).forEach(([sectionName]) => {
      if (hasRequiredFields(sectionName, application)) {
        completedSections++;
      }
    });
    
    // If the user has started but no sections are fully complete, return at least 5%
    if (completedSections === 0) return 5;
    
    // Calculate percentage completion
    const calculatedProgress = Math.round((completedSections / totalSections) * 100);
    console.log('Progress calculation:', { completedSections, totalSections, calculatedProgress });
    return calculatedProgress;
  };

  // Get an array of completed section indices for visual indicators
  const getCompletedSections = () => {
    if (!application) return [];
    
    // If the application is submitted, mark all sections as complete
    if (application.status === 'submitted') {
      return SECTIONS.map((_, index) => index);
    }
    
    return SECTIONS.map((section, index) => {
      // Skip the last section (Review and Submit)
      if (index === SECTIONS.length - 1) return false;
      
      const validationSection = VALIDATION_SECTIONS[section as keyof typeof VALIDATION_SECTIONS];
      return validationSection ? hasRequiredFields(validationSection, application) : false;
    })
    .map((isComplete, index) => isComplete ? index : null)
    .filter((index): index is number => index !== null);
  };

  return {
    calculateProgress,
    getCompletedSections,
  };
};
